import { get, set } from '@/helper/localStorage'
import _get from 'lodash/get'
import fields from '@/field/table/administrator'

export default {
  namespaced: true,
  state: {
    tableFields: get('administratorV2TableFields', fields),
    tableData: [],
    tablePagination: get('administratorV2TablePagination'),
    tableKeyword: get('administratorV2TableKeyword'),
    tableSort: get('administratorV2TableSort'),
    tableFilter: get('administratorV2TableFilter', []),
    detailData: get('administratorV2DetailData'),
    loadingTable: false,
    loadingDetail: false,
    loadingSubmit: false,
  },
  getters: {
    tableFields: state => {
      if (!state.tableFields) {
        return undefined
      }

      // only the visibility is customizable by the user
      const visibilities = state.tableFields.reduce(
        (obj, item) => Object.assign(obj, { [item.key]: item.visibility }), {},
      )

      // the other props uses the default fields configuration
      return fields.map(item => ({
        ...item,
        // only replace the visibility value if user override it
        visibility: item.key in visibilities ? visibilities[item.key] : item.visibility,
      }))
    },
    tableSort: state => {
      if (!state.tableSort) {
        return undefined
      }

      const symbol = state.tableSort.direction === 'desc' ? '-' : ''

      return `${symbol}${state.tableSort.key}`
    },
    tableData: state => {
      if (!state.tableData) {
        return []
      }

      return state.tableData.map(item => ({
        ...item,
        roles: Array.isArray(item.roles)
          ? item.roles.map(role => (typeof role === 'object' ? role.name : role))
          : item.roles,
      }))
    },
    detailData: state => {
      if (!state.detailData) {
        return undefined
      }

      return {
        ...state.detailData,
        roles: Array.isArray(state.detailData.roles)
          ? state.detailData.roles.map(role => (typeof role === 'object' ? role.name : role))
          : state.detailData.roles,
      }
    },
    tableFilter: state => {
      if (!state.tableFilter) {
        return []
      }

      return state.tableFilter
        .reduce((acc, elem) => Object.assign(acc, {
          [`filter[${elem.key}]`]: Array.isArray(elem.value)
            ? elem.value.join(',')
            : elem.value,
        }), {})
    },
    tableParams: (state, getters, rootState) => ({
      'filter[keyword]': state.tableKeyword,
      ...getters.tableFilter,
      sort: getters.tableSort,
      page: _get(state, 'tablePagination.current_page', 1),
      limit: rootState.appConfig.settings.tablePerPage,
    }),
  },
  mutations: {
    SET_TABLE_FIELDS(state, val) {
      set('administratorV2TableFields', val)
      state.tableFields = val
    },
    RESET_TABLE_FIELDS(state) {
      set('administratorV2TableFields', fields)
      state.tableFields = fields
    },
    SET_TABLE_DATA(state, val) {
      state.tableData = val
    },
    SET_TABLE_PAGINATION(state, val) {
      set('administratorV2TablePagination', val)
      state.tablePagination = val
    },
    SET_TABLE_KEYWORD(state, val) {
      set('administratorV2TableKeyword', val)
      state.tableKeyword = val
    },
    SET_DETAIL_DATA(state, val) {
      set('administratorV2DetailData', val)
      state.detailData = val
    },
    SET_TABLE_SORT(state, val) {
      set('administratorV2TableSort', val)
      state.tableSort = val
    },
    SET_TABLE_FILTER(state, val) {
      set('administratorV2TableFilter', val)
      state.tableFilter = val
    },
    SET_LOADING_TABLE(state, val) {
      state.loadingTable = val
    },
    SET_LOADING_DETAIL(state, val) {
      state.loadingDetail = val
    },
    SET_LOADING_SUBMIT(state, val) {
      state.loadingSubmit = val
    },
  },
  actions: {
    getTableData({ commit, getters }) {
      commit('SET_LOADING_TABLE', true)

      const { tableParams: params } = getters
      this._vm.$http.get('/v1/admin/administrators', { params })
        .then(({ data }) => {
          commit('SET_TABLE_DATA', data.data)
          commit('SET_TABLE_PAGINATION', data.meta)
        })
        .finally(() => {
          commit('SET_LOADING_TABLE', false)
        })
    },
    search(_, payload) {
      return this._vm.$http.get('/v1/admin/administrators', {
        params: {
          'filter[ids]': payload ? payload.ids : undefined,
          'filter[keyword]': payload ? payload.keyword : undefined,
        },
      })
        .then(({ data }) => data.data)
        .catch(() => [])
    },
    getDetail({ commit }, id) {
      commit('SET_LOADING_DETAIL', true)

      this._vm.$http.get(`/v1/admin/administrators/${id}`)
        .then(({ data }) => {
          commit('SET_DETAIL_DATA', data.data)
        })
        .finally(() => {
          commit('SET_LOADING_DETAIL', false)
        })
    },
    create({ commit }, payload) {
      commit('SET_LOADING_SUBMIT', true)

      return this._vm.$http.post('/v1/admin/administrators', payload)
        .finally(() => {
          commit('SET_LOADING_SUBMIT', false)
        })
    },
    update({ commit }, { id, data }) {
      commit('SET_LOADING_SUBMIT', true)

      return this._vm.$http.patch(`/v1/admin/administrators/${id}`, data)
        .then(response => {
          if (response?.data?.data) {
            commit('SET_DETAIL_DATA', response.data.data)
          }
        })
        .finally(() => {
          commit('SET_LOADING_SUBMIT', false)
        })
    },
    delete({ commit }, id) {
      commit('SET_LOADING_SUBMIT', true)

      return this._vm.$http.delete(`/v1/admin/administrators/${id}`)
        .finally(() => {
          commit('SET_LOADING_SUBMIT', false)
        })
    },
    forgotPassword({ commit }, payload) {
      commit('SET_LOADING_SUBMIT', true)

      return this._vm.$http.post('/v1/forgot-password', payload)
        .finally(() => {
          commit('SET_LOADING_SUBMIT', false)
        })
    },
    forceLogout({ commit }, userId) {
      commit('SET_LOADING_SUBMIT', true)

      return this._vm.$http.post('/v1/admin/force-logout', {
        user_id: userId,
      })
        .finally(() => {
          commit('SET_LOADING_SUBMIT', false)
        })
    },
    getRolesSelection(_, params) {
      return this._vm.$http.get('/v1/admin/roles/selection', { params })
        .then(({ data }) => data.data)
        .catch(() => [])
    },
  },
}
